/**
 * The conditions a pricing rule is actually evaluated against.
 *
 * A rule carries its conditions in one of two places: the legacy `conditions`
 * rows, or the `conditionGroups` Json the dashboard's "Add Price Modification"
 * dialog writes (that dialog stores everything in groups and sends
 * `conditions: []`). The engine's `evaluateConditionGroups` takes the groups
 * when there are any and falls back to `conditions` otherwise, so anything
 * describing a rule to the buyer has to read the same source in the same
 * order — reading `conditions` alone silently shows no hint at all for every
 * dialog-authored rule.
 *
 * Groups are OR'd against each other and their conditions AND'd inside, which
 * a flat list of labels cannot express. Hints are a summary, not the rule, so
 * flattening is deliberate: the buyer sees every condition that could apply.
 *
 * `conditionGroups` is free-form Json out of Mongo, so the rows are sanitised
 * before they reach the callers' label formatters: anything that isn't an
 * object with a string `conditionType` is dropped, and a missing `config` is
 * defaulted to `{}` so `c.config.minQuantity` can't throw on a malformed row.
 */
export function ruleEvaluatedConditions(rule: any): any[] {
  const groups = rule?.conditionGroups;
  const raw: any[] =
    Array.isArray(groups) && groups.length > 0
      ? groups.flatMap((g: any) => (Array.isArray(g?.conditions) ? g.conditions : []))
      : Array.isArray(rule?.conditions)
        ? rule.conditions
        : [];

  return raw
    .filter((c: any) => !!c && typeof c === "object" && typeof c.conditionType === "string")
    .map((c: any) => ({ ...c, config: c.config ?? {} }));
}

/**
 * The headline label for a rule in the ticket-step hint panels, or `null` when
 * the rule must not be advertised there at all.
 *
 * Those panels are a promo surface (green, "X% off" as the headline), so only
 * a rule that LOWERS the price earns a line. A rule that raises it — the
 * dialog's `percentage_increase` / `fixed_increase` — has to be dropped, not
 * relabelled: before this guard a "Last-minute pricing +20%" surcharge showed
 * up in the promo panel under its own name.
 *
 * Precedence mirrors the engine's `evaluateConditionalDiscount`
 * (`rule.modificationAction ?? legacyDiscountToAction(...)`): the dialog
 * writes the amount into `modificationAction` and leaves `discountType` /
 * `discountValue` null, while older rules only have the legacy pair. Reading
 * the legacy pair first would describe an amount the engine is not going to
 * apply whenever a rule somehow carries both.
 *
 * `currency` is the prefix the caller already renders money with — a symbol
 * where it has one, the raw code otherwise. Kept as the callers' own choice so
 * neither hint panel's copy changes.
 */
export function ruleDiscountHintLabel(rule: any, currency: string): string | null {
  if (rule?.ruleType !== "CONDITIONAL_DISCOUNT") return null;

  const action = rule.modificationAction;
  if (action && typeof action === "object") {
    const value = action.value;
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    switch (action.type) {
      case "percentage_decrease":
        return `${value}% off`;
      case "fixed_decrease":
        // Same unit as the price `applyModification` subtracts it from: pence.
        return `${currency}${(value / 100).toFixed(2)} off per ticket`;
      // `percentage_increase` / `fixed_increase` raise the price and an
      // `absolute_override` may be either, so none of them belong on a promo
      // panel.
      default:
        return null;
    }
  }

  const dt = rule.discountType;
  const dv = rule.discountValue;
  if (!dt || dv == null) return null;
  switch (dt) {
    case "percentage":
      return `${dv}% off`;
    case "fixed_per_unit":
      return `${currency}${(dv / 100).toFixed(2)} off per ticket`;
    case "fixed_total":
      return `${currency}${(dv / 100).toFixed(2)} off total`;
    case "price_override":
      return `${currency}${(dv / 100).toFixed(2)} per ticket`;
    // `DiscountType` is a closed union of the four above; anything else is a
    // type the engine's `legacyDiscountToAction` refuses to apply, so there is
    // nothing to advertise.
    default:
      return null;
  }
}
